import { useEffect, useState } from "react";
import {
  Modal,
  Select,
  TextInput,
  Textarea,
  NumberInput,
  Button,
  Stack,
  Group,
  Text,
  Alert,
  Loader,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconLink, IconAlertCircle } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { getFlows } from "../api";
import useToolStore from "../Tools/toolStore";

/**
 * Modal for quickly creating an agent tool that links to another flow.
 * Streamlines the agent tool creation process directly from the flow editor.
 */
export default function LinkAgentModal({
  opened,
  onClose,
  onToolCreated,
  excludeFlowId = null, // Exclude current flow from target options
}) {
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const { createTool } = useToolStore();

  // Fetch available flows for target selection
  useEffect(() => {
    if (opened) {
      setLoading(true);
      getFlows()
        .then((data) => {
          setFlows(data);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
        });
    }
  }, [opened]);

  const form = useForm({
    initialValues: {
      targetFlowId: "",
      name: "",
      description: "",
      maxAgentDepth: 5,
    },
    validate: {
      targetFlowId: (val) => (!val ? "Please select a target flow" : null),
      name: (val) =>
        !val || val.length < 3
          ? "Name must be at least 3 characters"
          : !/^[a-z0-9_]+$/.test(val)
          ? "Name must contain only lowercase letters, numbers, and underscores"
          : null,
    },
  });

  // Auto-generate name and description when flow is selected
  const handleFlowSelect = (flowId) => {
    form.setFieldValue("targetFlowId", flowId);

    const selectedFlow = flows.find((f) => f.id === flowId);
    if (selectedFlow) {
      // Generate a slug-friendly name from the flow name/title
      const slugName = (selectedFlow.name || selectedFlow.title || "agent")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_|_$/g, "");

      const generatedName = `call_${slugName}`;
      const generatedDescription = `Invokes the "${selectedFlow.title || selectedFlow.name}" flow as a sub-agent to handle specialized tasks.`;

      form.setFieldValue("name", generatedName);
      form.setFieldValue("description", generatedDescription);
    }
  };

  const handleSubmit = async (values) => {
    setCreating(true);
    try {
      const toolData = {
        name: values.name,
        description: values.description,
        toolType: "agent",
        agentTemplateId: values.targetFlowId,
        maxAgentDepth: values.maxAgentDepth,
        parameters: { properties: [], type: "object" },
      };

      const result = await createTool(toolData);

      notifications.show({
        title: "Agent tool created",
        message: `Tool "${values.name}" has been created and linked`,
        color: "green",
      });

      // Notify parent to add the tool to the template
      if (onToolCreated && result.tool) {
        onToolCreated(result.tool);
      }

      // Reset form and close modal
      form.reset();
      onClose();
    } catch (error) {
      notifications.show({
        title: "Failed to create tool",
        message: error.message || "An error occurred",
        color: "red",
      });
    } finally {
      setCreating(false);
    }
  };

  // Filter out current flow and build options
  const flowOptions = flows
    .filter((f) => f.id !== excludeFlowId)
    .map((f) => ({
      value: f.id,
      label: f.title || f.name,
    }));

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconLink size={20} />
          <Text fw={500}>Link Sub-Agent</Text>
        </Group>
      }
      size="md"
    >
      {loading ? (
        <Stack align="center" py="xl">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading flows...
          </Text>
        </Stack>
      ) : flows.length === 0 ? (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="No flows available"
          color="yellow"
        >
          Create some flows first before linking sub-agents.
        </Alert>
      ) : (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <Select
              label="Target Flow"
              description="Select the flow to invoke as a sub-agent"
              placeholder="Choose a flow"
              data={flowOptions}
              searchable
              required
              value={form.values.targetFlowId}
              onChange={handleFlowSelect}
              error={form.errors.targetFlowId}
            />

            <TextInput
              label="Tool Name"
              description="A unique identifier for this agent tool"
              placeholder="call_my_agent"
              required
              {...form.getInputProps("name")}
            />

            <Textarea
              label="Description"
              description="Describe what this agent does (helps the LLM decide when to use it)"
              placeholder="Invokes the agent to handle..."
              minRows={2}
              maxRows={4}
              autosize
              {...form.getInputProps("description")}
            />

            <NumberInput
              label="Max Recursion Depth"
              description="Limits how deeply nested agent calls can go (prevents infinite loops)"
              min={1}
              max={10}
              {...form.getInputProps("maxAgentDepth")}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={onClose} disabled={creating}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={creating}
                leftSection={<IconLink size={16} />}
              >
                Create & Link
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  );
}
